import { GeneralCondition, ReturnType } from "src/app/common/enumerations/enumerations";
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { utils } from "./utils";

export class ConditionParser {

	/**
	 * Resolves all values for a condition tag, including numeric radio values from partial execution data.
	 * @param tag Condition tag to resolve.
	 * @param tasks Tasks that may provide values for the tag.
	 * @param operator Operator controlling bare-value evaluation.
	 * @returns Values collected from every task with the matching tag.
	 */
	private static getValues(
		tag: string,
		tasks: WfTaskLocalResource[],
		operator: Operators = Operators.NONE
	) {
		const values: any[] = [];
		tasks
			.filter((t) => t.code?.trim() === tag.trim())
			.forEach((t) => {
				// Radio conditions must still resolve when execution data has partial component metadata.
				if (t.component?.hasRadioButtons || t.numericValue != null) {
					values.push(...this.getChecked(t, operator));
					return;
				}

				if (t.component?.returnType) {
					switch (t.component.returnType) {
						case ReturnType.Boolean:
							values.push(...(t.component.hasMultipleRoles
								? this.getRolesChecked(t, operator)
								: this.getBoolean(t, operator)));
							break;
						case ReturnType.Numeric:
							values.push(...this.getChecked(t, operator));
							break;
						case ReturnType.Sign:
							values.push(
								operator === Operators.NONE
									? t.wfTasks?.at(-1)?.signedBy != null
									: t.wfTasks?.at(-1)?.signedBy == null
							);
							break;
						case ReturnType.Text:
							values.push(...this.getText(t));
							break;
						default:
							values.push(t.value);
					}
				}
			});
		return values;
	}

	/**
	 * Gets stored text and radio selections as comparable values.
	 * @param task Task containing the stored component response.
	 * @returns Text values selected or entered for the task.
	 */
	private static getText(task: WfTaskLocalResource): any[] {
		let values: any[] = [];
		const storedText = utils.JSONparse(task.text);
		if (storedText?.length) {
			if (storedText.some((x: { key: any }) => x.key)) {
				const storedValues = storedText.find((x: { key: string }) => x.key === "values" || x.key === "radio")?.val;
				// Preserve radio text as one comparable value while supporting multi-value text components.
				values = typeof storedValues === "string" ? [storedValues] : storedValues?.filter((x: { text: any }) => x.text).map((x: { text: any }) => x.text) ?? [];
			} else {
				const storedString = utils.JSONparse(storedText[0].stringValue);
				const storedValues = storedString.find((x: { key: string }) => x.key === "values" || x.key === "radio")?.val;
				// Preserve radio text as one comparable value while supporting multi-value text components.
				values = typeof storedValues === "string" ? [storedValues] : storedValues?.filter((x: { text: any }) => x.text).map((x: { text: any }) => x.text) ?? [];
			}
		}
		return values;
	}

	private static getRolesChecked(
		task: WfTaskLocalResource,
		operator: Operators
	): any[] {
		let values: any[] = [];
		let roleOptions = [];
		const storedText = utils.JSONparse(task.text);
		if (task.text?.includes("stringValue")) {
			roleOptions = utils.JSONparse(storedText[0].stringValue);
		} else {
			roleOptions = storedText;
		}
		values.push(
			operator === Operators.NONE
				? roleOptions?.filter((o: { checked: any }) => o.checked).length ===
				roleOptions?.length
				: roleOptions?.filter((o: { checked: any }) => o.checked).length !==
				roleOptions?.length
		);
		return values;
	}

	private static getBoolean(
		task: WfTaskLocalResource,
		operator: Operators
	): any[] {
		const values: any[] = [];
		const storedText = utils.JSONparse(task.text);
		if (storedText?.length) {
			if (storedText.some((x: { key: any }) => x.key)) {
				const storedValues = storedText.find(
					(x: { key: string }) =>
						x.key === (operator === Operators.NONE ? "checkedYes" : "checkedNo")
				)?.val;
				values.push(storedValues);
			} else {
				const storedString = utils.JSONparse(storedText[0].stringValue);
				const storedValues = storedString?.find(
					(x: { key: string }) =>
						x.key === (operator === Operators.NONE ? "checkedYes" : "checkedNo")
				)?.val;
				values.push(storedValues);
			}
		}
		return values;
	}

	/**
	 * Gets the selected numeric component values in the same string format used by condition operands.
	 * @param task Task containing the stored component response.
	 * @param operator Operator used to evaluate the response.
	 * @returns Selected values or the component's boolean completion state.
	 */
	private static getChecked(task: WfTaskLocalResource, operator: Operators): any[] {
		const component = task.component;
		let values = [];
		if (component?.hasMultipleCheckboxes) {
			const storedText = utils.JSONparse(task.text);
			if (storedText?.length) {
				if (storedText.some((x: { key: any }) => x.key)) {
					const storedValues = storedText.find((x: { key: string }) => x.key === "checked")?.val;
					values = storedValues
						?.filter((x: { checked: any }) => x.checked)
						// Normalize numeric selections so conditions such as `B == 1` use consistent types.
						.map((x: { value: any }) => x.value?.toString()) ?? [];
				} else {
					const storedString = utils.JSONparse(storedText[0].stringValue);
					const storedValues = storedString?.find((x: { key: string }) => x.key === "checked")?.val;
					values = storedValues
						?.filter((x: { checked: any }) => x.checked)
						// Normalize numeric selections so conditions such as `B == 1` use consistent types.
						.map((x: { value: any }) => x.value?.toString()) ?? [];
				}
			}
		} else if (component?.hasRadioButtons || component?.returnType === ReturnType.Numeric) {
			// Bare numeric tags retain the KE/ABSI completion behavior; comparisons use the selected value.
			values.push(operator !== Operators.NONE
				? task.numericValue?.toString()
				: task.numericValue !== 0 && task.wfTasks?.at(-1)?.signedBy != null);
		} else {
			if (task.booleanValue !== undefined) {
				values.push(task.booleanValue);
			}
			if (task.isCompleted !== undefined) {
				values.push(task.isCompleted);
			}
		}
		return values;
	}

	/**
	 * Evaluates one tag, general condition, or comparison expression.
	 * @param condition Atomic condition without logical AND or OR operators.
	 * @param tasks Tasks that provide values for referenced tags.
	 * @param currentTask Task currently being processed for relative workflow conditions.
	 * @returns Whether the atomic condition is met.
	 */
	private static evaluateSingleCondition(condition: string, tasks: WfTaskLocalResource[], currentTask?: WfTaskLocalResource): boolean {
		const normalizedCondition = condition.trim();
		// Match longer comparison operators first so === and !== are not interpreted as == and !=.
		const comparison = normalizedCondition.match(/^(.+?)\s*(===|!==|==|!=)\s*(.+)$/);
		if (comparison) {
			const tag = comparison[1].trim();
			const operator = comparison[2] as Operators;
			const value = this.normalizeOperand(comparison[3]);
			if (!tag || value === null) return false;

			const matches = this.getValues(tag, tasks, operator).includes(value);
			return operator === Operators.EQUAL || operator === Operators.EQUAL2
				? matches
				: !matches;
		}

		if (normalizedCondition.startsWith(Operators.NOT)) {
			const tag = normalizedCondition.slice(1).trim();
			if (!tag) return false;

			const generalConditions = utils.getENUMString(GeneralCondition);
			// Negate the resolved condition, including workflow-wide general conditions.
			return generalConditions.map((x) => x.value).includes(tag)
				? !this.evalGeneralConditions(tag, tasks, currentTask)
				: !this.getValues(tag, tasks, Operators.NONE).includes(true);
		}

		const generalConditions = utils.getENUMString(GeneralCondition);
		if (generalConditions.map((x) => x.value).includes(normalizedCondition)) {
			return this.evalGeneralConditions(normalizedCondition, tasks, currentTask);
		}

		return this.getValues(normalizedCondition, tasks, Operators.NONE).includes(true);
	}

	/**
	 * Normalizes quoted and unquoted comparison operands.
	 * @param operand Operand text from a comparison expression.
	 * @returns Comparable operand value, or null when the operand is empty.
	 */
	private static normalizeOperand(operand: string): string | null {
		const value = operand.trim();
		if (!value) return null;

		const quote = value[0];
		if ((quote === "'" || quote === '"') && value[value.length - 1] === quote) {
			return value.slice(1, -1);
		}
		return value;
	}


	private static evalGeneralConditions(
		condition: string,
		tasks: WfTaskLocalResource[],
		currentTask?: WfTaskLocalResource
	) {
		let result = true;
		switch (condition) {
			case GeneralCondition.AllTasksCompleted:
				result = tasks
					.filter((t) => t.condition !== condition)
					.every((t) => !t.required || t.isCompleted);
				break;

			case GeneralCondition.PreviousTasksCompleted: {
				const currentIndex = currentTask ? tasks.indexOf(currentTask) : -1;
				result = currentIndex <= 0
					|| tasks.slice(0, currentIndex).every((t) => !t.required || t.isCompleted);
				break;
			}

			case GeneralCondition.AnySubsequentTaskSigned: {
				const currentIndex = currentTask ? tasks.indexOf(currentTask) : -1;
				result = currentIndex >= 0
					&& tasks.slice(currentIndex + 1).some((t) => t.wfTasks?.at(-1)?.signedBy != null);
				break;
			}

			case GeneralCondition.SigConditionMet:
				result = currentTask?.sigConditionResult ?? false;
				break;

			default:
				return true;
		}
		return result;
	}

	/**
	 * Splits an expression by a logical operator while respecting quotes and parentheses.
	 * @param condition Expression to split.
	 * @param operator Top-level logical operator.
	 * @returns Expression segments, or an empty array when syntax is invalid.
	 */
	private static splitTopLevel(condition: string, operator: Operators.AND | Operators.OR): string[] {
		const parts: string[] = [];
		let buffer = "";
		let depth = 0;
		let quote: string | null = null;

		for (let i = 0; i < condition.length; i++) {
			const char = condition[i];
			if (quote) {
				buffer += char;
				if (char === quote && condition[i - 1] !== "\\") quote = null;
				continue;
			}
			if (char === "'" || char === '"') {
				quote = char;
				buffer += char;
			} else if (char === "(") {
				depth++;
				buffer += char;
			} else if (char === ")") {
				depth--;
				if (depth < 0) return [];
				buffer += char;
			} else if (depth === 0 && condition.startsWith(operator, i)) {
				parts.push(buffer.trim());
				buffer = "";
				i++;
			} else {
				buffer += char;
			}
		}

		if (depth !== 0 || quote) return [];
		parts.push(buffer.trim());
		return parts;
	}

	/**
	 * Determines whether one pair of parentheses encloses the complete expression.
	 * @param condition Expression that starts and ends with parentheses.
	 * @returns Whether the outer parentheses are a complete wrapper.
	 */
	private static hasWrappingParentheses(condition: string): boolean {
		if (!condition.startsWith("(") || !condition.endsWith(")")) return false;

		let depth = 0;
		let quote: string | null = null;
		for (let i = 0; i < condition.length; i++) {
			const char = condition[i];
			if (quote) {
				if (char === quote && condition[i - 1] !== "\\") quote = null;
				continue;
			}
			if (char === "'" || char === '"') quote = char;
			else if (char === "(") depth++;
			else if (char === ")") {
				depth--;
				if (depth === 0 && i < condition.length - 1) return false;
			}
		}
		return depth === 0 && !quote;
	}

	/**
	 * Evaluates an expression with standard AND-before-OR precedence.
	 * @param condition Condition expression or parenthesized subexpression.
	 * @param tasks Tasks that provide values for referenced tags.
	 * @param currentTask Task currently being processed for relative workflow conditions.
	 * @returns Whether the expression is valid and met.
	 */
	private static evaluateExpression(condition: string, tasks: WfTaskLocalResource[], currentTask?: WfTaskLocalResource): boolean {
		let expression = condition.trim();
		if (!expression) return false;

		while (this.hasWrappingParentheses(expression)) {
			expression = expression.slice(1, -1).trim();
		}

		const orParts = this.splitTopLevel(expression, Operators.OR);
		if (!orParts.length || orParts.some((part) => !part)) return false;
		if (orParts.length > 1) {
			return orParts.some((part) => this.evaluateExpression(part, tasks, currentTask));
		}

		const andParts = this.splitTopLevel(expression, Operators.AND);
		if (!andParts.length || andParts.some((part) => !part)) return false;
		if (andParts.length > 1) {
			return andParts.every((part) => this.evaluateExpression(part, tasks, currentTask));
		}

		return this.evaluateSingleCondition(expression, tasks, currentTask);
	}

	/**
	 * Evaluates a condition expression against workflow tasks.
	 * @param condition Condition expression to evaluate.
	 * @param tasks Tasks that provide values for referenced tags.
	 * @param currentTask Task currently being processed for relative workflow conditions.
	 * @returns Whether the condition is valid and met.
	 */
	static parseCondition(condition: string, tasks: WfTaskLocalResource[], currentTask?: WfTaskLocalResource): boolean {
		if (!condition) return true;
		return this.evaluateExpression(condition, tasks, currentTask);
	}


	/**
	 * Applies condition results as either hidden or disabled task state.
	 * @param tasks Workflow tasks to evaluate and update.
	 * @returns The evaluated task collection.
	 */
	static parseConditions(tasks: WfTaskLocalResource[]): WfTaskLocalResource[] {
		return tasks.map((t) => {

			// console.warn(`Task ${t.id} - Condition: ${t.condition}`);

			const value = t.condition ? this.parseCondition(t.condition, tasks, t) : true;
			const hideDisable = t.configuration
				? utils.JSONparse(t.configuration)?.hideDisable ?? false
				: false;

			// console.log(`Task ${t.id} - Condition: ${t.condition}, Value: ${value}`);
			// console.log(`Task ${t.id} - Required: ${!hideDisable ? value : true}`);
			// console.log(`Task ${t.id} - Disabled: ${hideDisable ? !value : false}`);
			// console.log(`------------------------------------------------------------------------------------`);

			// hideDisable=true selects Disable; the default false value selects Hide.
			t.required = hideDisable ? true : value;
			t.disabled = hideDisable && !value;

			const latestWfTask = t.wfTasks?.at(-1);
			if (latestWfTask) {
				latestWfTask.required = t.required;
				latestWfTask.enabled = !t.disabled;
			}
			t.isCompleted = t.required ? utils.taskComplete(t) : true;
			return t;
		});
	}
}

export enum Operators {
	EQUAL = "==",
	EQUAL2 = "===",
	NOTEQUAL = "!=",
	NOTEQUAL2 = "!==",
	AND = "&&",
	OR = "||",
	NOT = "!",
	NONE = "",
}
